import { useMotionValue, useTransform } from "framer-motion";
import { usePathname } from "next/navigation";
import { useEffect } from "react";

/**
 * 根据滚动位置计算标题紧凑进度
 * 使用 framer-motion 的 motionValue，避免 React 重渲染导致的抖动
 * @param start - 开始收缩的滚动距离
 * @param range - 收缩过渡距离
 * @returns motionValue，可直接用于 motion 组件
 */
export function useTitleCompact( start: number = 40, range: number = 120 ) {
    const pathname = usePathname();
    const scrollY = useMotionValue( 0 );

    // 使用 useTransform 将滚动位置映射到 0-1 的进度
    // 添加 8px 的激活偏移量
    const activationOffset = 8;
    const compactProgress = useTransform(
        scrollY,
        [ start + activationOffset, start + activationOffset + range ],
        [ 0, 1 ],
    );

    useEffect( () => {
        // 优先使用事件详情页的内部滚动容器，回退到 Layout 的滚动容器
        const eventDetailScrollable = document.querySelector(
            ".event-detail-scrollable",
        ) as HTMLElement | null;
        const pullToRefreshContainer = document.querySelector(
            ".pull-to-refresh-container",
        ) as HTMLElement | null;
        const scrollContainer = eventDetailScrollable || pullToRefreshContainer;

        const updateScrollY = () => {
            const current = scrollContainer ? scrollContainer.scrollTop : window.scrollY;
            scrollY.set( current );
        };

        // 初始化滚动位置
        updateScrollY();

        // 监听滚动事件
        const handleScroll = () => {
            requestAnimationFrame( updateScrollY );
        };

        if ( scrollContainer ) {
            scrollContainer.addEventListener( "scroll", handleScroll, { passive: true } );
        } else {
            window.addEventListener( "scroll", handleScroll, { passive: true } );
        }

        return () => {
            if ( scrollContainer ) {
                scrollContainer.removeEventListener( "scroll", handleScroll );
            } else {
                window.removeEventListener( "scroll", handleScroll );
            }
        };
    }, [ pathname, scrollY ] );

    return compactProgress;
}
